import React, { useState, useEffect } from 'react';
import imageBannerService, { ImageBanner } from '../services/imageBannerService';
import ContentModal from './ContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const BannerManagement: React.FC = () => {
  const [banners, setBanners] = useState<ImageBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Partial<ImageBanner> | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<ImageBanner | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const data = await imageBannerService.getAllImageBanners(true);
      // Sort by title in ascending order
      const sortedData = (data || []).sort((a, b) => 
        a.title.localeCompare(b.title)
      );
      setBanners(sortedData);
      setError(null);
      setImageRefreshKey(Date.now());
    } catch (err) {
      setError('Failed to fetch banners');
      console.error('Error fetching banners:', err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingBanner({
      title: '',
      description: '',
      link: '',
      is_visible: true,
      sort_order: banners.length + 1
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (banner: ImageBanner) => {
    setEditingBanner({ ...banner });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
    setSelectedFile(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setSaveMessage('Error: Please select an image file');
        setTimeout(() => setSaveMessage(''), 3000);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setSaveMessage('Error: Image size must be less than 25MB');
        setTimeout(() => setSaveMessage(''), 3000);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSave = async () => {
    if (!editingBanner || !editingBanner.title || !editingBanner.description) {
      setSaveMessage('Error: Title and Description are required');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    if (!editingBanner.id && !selectedFile) {
      setSaveMessage('Error: Image is required for new banners');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      formData.append('title', editingBanner.title);
      formData.append('description', editingBanner.description);
      if (editingBanner.link) formData.append('link', editingBanner.link);
      formData.append('is_visible', String(editingBanner.is_visible ?? true));
      formData.append('sort_order', String(editingBanner.sort_order || banners.length + 1));
      
      if (selectedFile) {
        formData.append('image', selectedFile);
      }

      if (editingBanner.id) {
        await imageBannerService.updateImageBanner(editingBanner.id, formData);
        setSaveMessage('Banner saved successfully');
      } else {
        await imageBannerService.createImageBanner(formData);
        setSaveMessage('Banner created successfully');
      }
      
      await fetchBanners();
      handleCloseModal();
      setCurrentPage(1);
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error saving banner');
      console.error('Error saving banner:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (banner: ImageBanner) => {
    setBannerToDelete(banner);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    try {
      setSubmitting(true);
      await imageBannerService.deleteImageBanner(bannerToDelete.id);
      await fetchBanners();
      setCurrentPage(1);
      setSaveMessage('Banner deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error deleting banner');
      console.error('Error deleting banner:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVisibility = async (id: number) => {
    try {
      await imageBannerService.toggleVisibility(id);
      await fetchBanners();
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  // Filter banners based on search query
  const filteredBanners = banners.filter(banner => {
    const query = searchQuery.toLowerCase();
    return banner.title.toLowerCase().includes(query);
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBanners = filteredBanners.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBanners.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading banners...</div>;
  }

  return (
    <div className="edit-section">
      <div className="applications-list">
        <div className="list-header">
          <h4>Image Banner Management</h4>
          <button 
            className="btn-add" 
            onClick={handleAddClick}
            disabled={submitting}
          >
            <i className="icon-plus"></i>
            Add Banner
          </button>
        </div>

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
            border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
            position: 'relative'
          }}>
            {saveMessage}
            <button
              onClick={() => setSaveMessage('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '8px',
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                padding: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close message"
            >
              ×
            </button>
          </div>
        )}

        {error && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>{error}</div>}

        {/* Search Bar */}
        <div style={{ marginTop: '20px', marginBottom: '15px' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                paddingLeft: '35px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                width: '100%',
                padding: '8px 8px 8px 35px'
              }}
            />
            <i 
              className="fas fa-search" 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d'
              }}
            ></i>
          </div>
          {searchQuery && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
              Found {filteredBanners.length} banner{filteredBanners.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '10%' }}>Image</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '25%' }}>Title</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '35%' }}>Description</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '10%' }}>Visible</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Order</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentBanners.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                    {searchQuery ? 'No banners found matching your search' : 'No banners found. Click "Add Banner" to create one.'}
                  </td>
                </tr>
              ) : (
                currentBanners.map((banner) => (
                  <tr 
                    key={banner.id}
                    style={{ 
                      borderBottom: '1px solid #ddd'
                    }}
                  >
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <img 
                        src={banner.image ? `${imageBannerService.getImageUrl(banner.image)}?t=${imageRefreshKey}` : '/assets/images/banner-image.jpg'} 
                        alt={banner.title}
                        style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/assets/images/banner-image.jpg';
                        }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong>{banner.title}</strong>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>{banner.description ? banner.description.substring(0, 100) : ''}{banner.description && banner.description.length > 100 ? '...' : ''}</span>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={banner.is_visible}
                        onChange={() => handleToggleVisibility(banner.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {banner.sort_order}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleEditClick(banner)}
                          disabled={submitting}
                          title="Edit banner"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#e3f2fd')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          ✏️
                        </button>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleDeleteClick(banner)}
                          disabled={submitting}
                          title="Delete banner"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#ffebee')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filteredBanners.length > itemsPerPage && (
            <div style={{ 
              marginTop: '20px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '10px 0'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBanners.length)} of {filteredBanners.length} banners
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#007bff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      backgroundColor: currentPage === page ? '#007bff' : 'white',
                      color: currentPage === page ? 'white' : '#007bff',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: currentPage === page ? 'bold' : 'normal'
                    }}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                    color: currentPage === totalPages ? '#6c757d' : '#007bff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingBanner?.id ? 'Edit Banner' : 'Add New Banner'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Title <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={editingBanner?.title || ''}
              onChange={(e) => setEditingBanner(prev => ({ ...prev, title: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="Enter banner title"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Description <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={editingBanner?.description || ''}
              onChange={(e) => setEditingBanner(prev => ({ ...prev, description: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '80px' }}
              placeholder="Enter banner description"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Link (Optional)</label>
            <input
              type="url"
              value={editingBanner?.link || ''}
              onChange={(e) => setEditingBanner(prev => ({ ...prev, link: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Banner Image {!editingBanner?.id && <span style={{ color: 'red' }}>*</span>}
            </label>
            <input
              id="bannerImageInput"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Recommended size: 1200×400 pixels (3:1 ratio). Max 5MB.
            </p>
            {editingBanner?.image && !selectedFile && (
              <div style={{ marginTop: '8px' }}>
                <img 
                  src={`${imageBannerService.getImageUrl(editingBanner.image)}?t=${Date.now()}`}
                  alt="Current"
                  style={{ width: '200px', height: 'auto', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                  onError={(e) => {
                    console.error('Failed to load image:', editingBanner.image);
                    (e.target as HTMLImageElement).src = '/assets/images/banner-image.jpg';
                  }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Current image</p>
              </div>
            )}
            {selectedFile && (
              <div style={{ marginTop: '8px' }}>
                <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>New Image (Cropped):</p>
                <img 
                  src={URL.createObjectURL(selectedFile)}
                  alt="Cropped preview"
                  style={{ width: '300px', height: 'auto', objectFit: 'cover', borderRadius: '4px', border: '2px solid #00a0e3' }}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="is_visible"
              checked={editingBanner?.is_visible ?? true}
              onChange={(e) => setEditingBanner(prev => ({ ...prev, is_visible: e.target.checked }))}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="is_visible" style={{ cursor: 'pointer', fontWeight: '500' }}>Visible on homepage</label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
            <button
              onClick={handleCloseModal}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#00a0e3',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </ContentModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Image Banner"
        message="Are you sure you want to delete this banner?"
        itemName={bannerToDelete?.title}
      />
    </div>
  );
};

export default BannerManagement;
